import uuid from 'react-uuid';
import { IGLCode } from 'src/types/GLCode';
import { IPoLineItems } from 'src/types/PoLineItems';
import { IPostBody } from 'src/types/PostBody';
import axios from 'src/utils/axios';

import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { RootState } from 'src/app/store';
import { ReceiptLineItem } from 'src/services/useReceiptLineItemsApi';
interface ILineItemParams {
  id: number | string | undefined;
  postBody: IPostBody;
}

type IGLCodeInputParams = {
  companyId: number | string;
  glPeriodId?: string | null;
};

interface IDeleteLineItemParams {
  recId: number | string | undefined;
  recLineItemId: number | string | undefined;
}

export const fetchReceiptLineItemsData = createAsyncThunk(
  'lineItems/fetchReceiptLineItemsData',
  async (id: number) => {
    const response = await axios.get(`/Receipt/${id}/LineItems`);
    return response.data;
  }
);
export const fetchPOLineItemsData = createAsyncThunk(
  'lineItems/fetchPOLineItemsData',
  async ({ id, postBody }: ILineItemParams) => {
    const response = await axios.post(`/po/${id}/lineItems/filter`, postBody);
    return response.data;
  }
);

export const fetchGLCodes = createAsyncThunk(
  'lineItems/fetchGLCodes',
  async ({ companyId, glPeriodId }: IGLCodeInputParams) => {
    const response = await axios.get('/GLCode', { params: { companyId: companyId } });
    return response.data;
  }
);

export const deleteReceiptLineItem: any = createAsyncThunk(
  'lineItems/deleteReceiptLineItem',
  async ({ recId, recLineItemId }: IDeleteLineItemParams) => {
    const response = await axios.delete(`/Receipt/${recId}/LineItem/${recLineItemId}`);
    return response;
  }
);

interface gridLineItemsDataState {
  receiptLineItemsData: {
    data: ReceiptLineItem[];
    total: number;
  };
  receiptLineItemsLoading: 'loading' | 'pending' | 'succeeded' | 'failed';
  poLineItemsData: {
    data: IPoLineItems[];
    total: number;
  };
  poLineItemsLoading: 'loading' | 'pending' | 'succeeded' | 'failed';
  glCodesData: IGLCode[];
  glCodesLoading: 'loading' | 'pending' | 'succeeded' | 'failed';
  deletedReceiptLineItemId: string | number;
  deleteReceiptLineItemLoading: 'loading' | 'pending' | 'succeeded' | 'failed';
}

const initialState = {
  receiptLineItemsData: { data: [], total: 0 },
  receiptLineItemsLoading: 'loading',
  poLineItemsData: { data: [], total: 0 },
  poLineItemsLoading: 'loading',
  glCodesData: [],
  glCodesLoading: 'loading',
  deletedReceiptLineItemId: '',
  deleteReceiptLineItemLoading: 'loading',
} as gridLineItemsDataState;

export const lineItemsSlice = createSlice({
  name: 'lineItems',
  initialState,
  reducers: {
    // standard reducer logic, with auto-generated action types per reducer
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        fetchReceiptLineItemsData.fulfilled,
        (state, action: PayloadAction<{ items: ReceiptLineItem[]; totalRecords: number }>) => {
          state.receiptLineItemsData = {
            data: action.payload.items,
            total: action.payload.totalRecords,
          };
          state.receiptLineItemsLoading = 'succeeded';
        }
      )
      .addCase(fetchReceiptLineItemsData.rejected, (state) => {
        state.receiptLineItemsLoading = 'failed';
      })
      .addCase(fetchReceiptLineItemsData.pending, (state) => {
        state.receiptLineItemsLoading = 'pending';
      })
      .addCase(
        fetchPOLineItemsData.fulfilled,
        (state, action: PayloadAction<{ items: IPoLineItems[]; totalRecords: number }>) => {
          state.poLineItemsData = {
            data: action.payload.items,
            total: action.payload.totalRecords,
          };
          state.poLineItemsLoading = 'succeeded';
        }
      )
      .addCase(fetchPOLineItemsData.rejected, (state) => {
        state.poLineItemsLoading = 'failed';
      })
      .addCase(fetchPOLineItemsData.pending, (state) => {
        state.poLineItemsLoading = 'pending';
      })
      .addCase(fetchGLCodes.fulfilled, (state, action: PayloadAction<IGLCode[]>) => {
        state.glCodesData = action.payload;
        state.glCodesLoading = 'succeeded';
      })
      .addCase(fetchGLCodes.rejected, (state) => {
        state.glCodesLoading = 'failed';
      })
      .addCase(fetchGLCodes.pending, (state) => {
        state.glCodesLoading = 'pending';
      })
      .addCase(deleteReceiptLineItem.fulfilled, (state, action: PayloadAction<any>) => {
        state.deletedReceiptLineItemId = action.payload;
        state.deleteReceiptLineItemLoading = 'succeeded';
      })
      .addCase(deleteReceiptLineItem.rejected, (state) => {
        state.deleteReceiptLineItemLoading = 'failed';
      })
      .addCase(deleteReceiptLineItem.pending, (state) => {
        state.deleteReceiptLineItemLoading = 'pending';
      });
  },
});

export const selectLineItems = (state: RootState) => {
  const { lineItems } = state;
  return {
    ...lineItems,
    receiptLineItemsData: {
      data: lineItems.receiptLineItemsData.data.map((receiptLineItem: ReceiptLineItem) => ({
        ...receiptLineItem,
        item: receiptLineItem?.item || '',
        itemDescription: receiptLineItem?.itemDescription || '',
        poglCode: receiptLineItem?.poglCode || '',
        uom: receiptLineItem?.uom || '',
        rowId: uuid(),
      })),
      total: lineItems.receiptLineItemsData.total,
    },
    glCodesData: lineItems.glCodesData.map((glCode: any) => ({
      text: `${glCode?.code} - ${glCode?.description}. Balance: $${glCode?.remainingBudgetAmount}`,
      value: glCode?.code,
    })),
  };
};
export default lineItemsSlice.reducer;
